<template>
    <div>
        <limitless-page-header title="Inlösensstatistik" />
        <div class="content">
            <div class="card">
                <div class="card-header"><h5 class="card-title">Urval</h5></div>
                <div class="card-body">
                    <ValidationObserver ref="filtersform">
                        <form @submit.prevent="getReport">
                            <div class="row">
                                <div class="col-md-3">
                                    <input-with-validation v-model="filters.series" vid="series" label="Kortserie" rules="required|numeric" :disabled="loading" />
                                </div>
                                <div class="col-md-3">
                                    <input-with-validation v-model="filters.dateStart" vid="dateStart" label="Från datum" type="date" rules="required" :disabled="loading" />
                                </div>
                                <div class="col-md-3">
                                    <input-with-validation v-model="filters.dateEnd" vid="dateEnd" label="Till datum" type="date" rules="required" :disabled="loading" />
                                </div>
                                <div class="col-md-3">
                                    <label class="d-block">Gruppering</label>
                                    <div class="custom-control custom-radio custom-control-inline">
                                        <input id="group-month" v-model="filters.grouping" class="custom-control-input" type="radio" name="grouping" value="m" :disabled="loading">
                                        <label class="custom-control-label" for="group-month">Månad</label>
                                    </div>
                                    <div class="custom-control custom-radio custom-control-inline">
                                        <input id="group-week" v-model="filters.grouping" class="custom-control-input" type="radio" name="grouping" value="w" :disabled="loading">
                                        <label class="custom-control-label" for="group-week">Vecka</label>
                                    </div>
                                </div>
                            </div>
                            <limitless-button type="submit" icon="icon-stats-bars" :busy="loading">Hämta statistik</limitless-button>
                        </form>
                    </ValidationObserver>
                </div>
            </div>

            <div v-if="error_message" class="alert alert-danger" role="alert">{{ error_message }}</div>
            <div v-if="loading" class="text-center mb-3" role="status"><i class="icon-spinner2 spinner mr-2"></i>Hämtar statistik...</div>

            <template v-if="report && !loading">
                <h2>Kortserie {{ report.filters.series }}, {{ report.filters.dateStart }} – {{ report.filters.dateEnd }}</h2>
                <p class="text-muted">Genererad: {{ report.generated_at }}</p>

                <rednax-datatable
                    v-for="section in sections"
                    :key="section.key"
                    :data="section.rows"
                    :columns="section.columns"
                    :sort-by="section.sortBy"
                    :sort-order="section.sortOrder"
                >
                    <template #header>
                        <div>
                            <h5 class="card-title">{{ section.title }}</h5>
                            <p class="text-muted mb-0">{{ section.description }}</p>
                        </div>
                    </template>
                    <template #cell_product_id="{ row, cell }">
                        <router-link v-if="cell > 0" :to="'/products/' + row.product_id + '/logistics'">{{ cell }}</router-link>
                        <span v-else>{{ cell == null ? 'Saknas' : cell }}</span>
                    </template>
                    <template #cell_batch="{ row }"><a :href="batchLink(row)">Visa lista</a></template>
                    <template #cell_count="{ cell }">{{ formatNumber(cell, 0) }}</template>
                    <template #cell_period="{ cell }">{{ cell || 'Saknas' }}</template>
                    <template #cell_source="{ cell }">{{ cell || 'Saknas' }}</template>
                    <template #cell_domain="{ cell }">{{ cell || 'Saknas' }}</template>
                    <template #cell_name="{ cell }">{{ cell || 'Saknas' }}</template>
                    <template #cell_type="{ cell }">{{ cell || 'Saknas' }}</template>
                    <template #tfoot>
                        <tr>
                            <th :colspan="section.columns.length" class="text-right">Totalt: {{ formatNumber(report.reports[section.key].total, 0) }}</th>
                        </tr>
                    </template>
                </rednax-datatable>
            </template>
        </div>
    </div>
</template>

<script>
import LimitlessPageHeader from '@components/Layout/partials/LimitlessPageHeader';
import InputWithValidation from '@components/Form/InputWithValidation';
import LimitlessButton from '@components/Limitless/LimitlessButton';
import RednaxDatatable from '@components/Datatable/RednaxDatatableComponent';
import apiClient from '@services/ApiClient';
import { formatNumber } from '@services/Helpers';

function dateString(date) {
    return [date.getFullYear(), String(date.getMonth() + 1).padStart(2, '0'), String(date.getDate()).padStart(2, '0')].join('-');
}

function defaultFilters() {
    const today = new Date();
    const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
    return {
        series: '',
        dateStart: (today.getFullYear() - 1) + '-01-01',
        dateEnd: dateString(yesterday),
        grouping: 'm',
    };
}

export default {
    name: 'RedeemStats',
    components: {
        LimitlessPageHeader, InputWithValidation, LimitlessButton, RednaxDatatable,
    },
    data() {
        return {
            filters: defaultFilters(),
            loading: false,
            refresh_pending: false,
            page_active: true,
            report: null,
            error_message: '',
        };
    },
    mounted() {
        this.readQuery();
    },
    beforeDestroy() {
        this.page_active = false;
    },
    watch: {
        '$route.query': function queryChanged(query) {
            if (this.report && Object.keys(this.report.filters).every((key) => query[key] === this.report.filters[key])) {
                return;
            }
            this.readQuery();
        },
    },
    computed: {
        sections() {
            const unit = this.report?.filters.grouping === 'w' ? 'vecka' : 'månad';
            const period = { key: 'period', label: unit === 'vecka' ? 'Vecka' : 'Månad' };
            const count = { key: 'count', label: 'Antal', numeric: true };
            const product = { key: 'product_id', label: 'Produkt-ID', numeric: true };
            const name = { key: 'name', label: 'Produkt' };
            return [
                {
                    key: 'redeemed', title: 'Inlösen per ' + unit, description: 'Antal inlösen som kommit in i datumintervallet, fördelat på källa.', columns: [period, { key: 'source', label: 'Källa' }, count],
                },
                {
                    key: 'wanted', title: 'Önskade leveranser per ' + unit, description: 'Inlösen i datumintervallet fördelat på önskat leveransdatum.', columns: [period, count],
                },
                {
                    key: 'delivered', title: 'Faktiska leveranser per ' + unit, description: 'Order med minst status Skickad (400), fördelade på Packtrack-datum. Saknat datum visas separat.', columns: [period, count],
                },
                {
                    key: 'products', title: 'Produkter', description: 'Produktval på inlösta kort. Antalet avser produktrader.', columns: [product, name, { key: 'type', label: 'Typ' }, count],
                },
                {
                    key: 'backorders',
                    title: 'Restprodukter',
                    description: 'Produkter på order med status under Skickad (400) och önskat leveransdatum före igår. Innehåller även returer till Supékort.',
                    columns: [product, name, count, { key: 'batch', label: 'Leveranslista' }],
                },
                {
                    key: 'resent', title: 'Omsändningar/reklamationer per ' + unit, description: 'Antal kollin utöver det första, per kort och period, med minst EDI-status i Packtrack.', columns: [period, count],
                },
                {
                    key: 'email', title: 'E-post', description: 'Antal inlösare per e-postdomän.', columns: [{ key: 'domain', label: 'Domän' }, count],
                },
            ].map((section) => {
                const byCount = ['products', 'backorders'].includes(section.key);
                let sortBy = 'period';
                if (byCount) sortBy = 'count';
                if (section.key === 'email') sortBy = 'domain';

                return {
                    ...section,
                    sortBy,
                    sortOrder: byCount ? 'desc' : 'asc',
                    columns: section.columns.map((column) => ({
                        data: column.key,
                        header: column.label,
                        sortable: column.key !== 'batch',
                        align_right: Boolean(column.numeric),
                    })),
                    rows: (this.report?.reports[section.key].rows || []).map((row) => ({
                        ...row,
                        count: Number(row.count),
                        product_id: row.product_id == null ? null : Number(row.product_id),
                    })),
                };
            });
        },
    },
    methods: {
        formatNumber,
        readQuery() {
            if (this.loading) {
                this.refresh_pending = true;
                return;
            }
            const query = this.$route.query;
            this.filters = defaultFilters();
            Object.keys(this.filters).forEach((key) => {
                if (typeof query[key] === 'string') {
                    this.filters[key] = query[key];
                }
            });
            if (this.filters.series) {
                this.getReport();
            } else {
                this.report = null;
            }
        },
        async getReport() {
            if (this.loading) {
                return;
            }
            this.loading = true;
            this.error_message = '';
            try {
                if (!await this.$refs.filtersform.validate()) {
                    return;
                }
                const response = await apiClient.get('/vue/stats/redeem', { params: { ...this.filters } });
                if (!this.page_active || this.refresh_pending) {
                    return;
                }
                this.report = response.data;
                this.filters = { ...response.data.filters };
                this.$refs.filtersform.reset();
                if (!Object.keys(this.filters).every((key) => this.$route.query[key] === this.filters[key])) {
                    await this.$router.replace({ name: 'stats.redeem', query: { ...this.filters } });
                }
            } catch (error) {
                if (error.response?.status === 422 && error.response.data?.errors) {
                    this.$refs.filtersform.setErrors(error.response.data.errors);
                    this.error_message = 'Kontrollera de markerade fälten.';
                } else {
                    this.error_message = 'Statistiken kunde inte hämtas. Försök igen.';
                }
                this.report = null;
            } finally {
                this.loading = false;
                if (this.page_active && this.refresh_pending) {
                    this.refresh_pending = false;
                    this.readQuery();
                }
            }
        },
        batchLink(row) {
            const query = new URLSearchParams({
                type: 'prod_order',
                func: 'print',
                card_series_id: this.report.filters.series,
                prod_count: 'single',
                date_from: this.report.filters.dateStart,
                date_to: this.report.filters.dateEnd,
                prod_id: row.product_id,
            });
            return '/batch/list?' + query.toString();
        },
    },
};
</script>
